import { Middleware, Dispatch, AnyAction } from 'redux';
import { HttpClient } from 'support/api/api-types';

/*
This middleware is to allow for us to intercept actions that contain promises,
and propagate forward new actions that are more stateful.  If an action contains
a promise attribute, we'll propagate that action forward as a LOADING action, and
only dispatch the original action once we get a resolve from that promise, within
which we'll attach the payload passed into the resolve method.  If the promise
rejects, we'll dispatch an ERROR action.
*/

export interface PromiseAction<T = unknown> extends AnyAction {
  promise?: Promise<T> | ((httpClient: HttpClient) => Promise<T>);
}

export type PromiseDispatch = <T>(action: PromiseAction<T>) => Promise<T | boolean>;

const promiseMiddleware = (httpClient: HttpClient): Middleware =>
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  (action: PromiseAction) => {
    const { promise, ...rest } = action;

    if (!promise) {
      return next(action);
    }

    const newAction = {
      ...action,
      type: `${action.type}_LOADING`,
    };

    next(newAction);

    const getPromise = typeof promise === 'function' ? promise : () => promise;
    return getPromise(httpClient).then(
      response => {
        dispatch({
          ...rest,
          payload: response,
        });
        return response || true;
      },
      error => {
        dispatch({
          ...rest,
          type: `${action.type}_ERROR`,
          error: true,
          payload: error,
        });
        return false;
      },
    );
  };

export default promiseMiddleware;
